import { useEffect, useMemo, useState } from 'react';
import { Chart as ReactChart } from 'react-chartjs-2';
import type { ChartData, ChartOptions, ChartType } from 'chart.js';
import type { ChartPayload } from '@oxxean/types';
import { setupCharts } from '../lib/chart-setup';
import { buildChartOptions, inferChartFormat, readChartTheme, withAlpha, type ChartTheme } from '../lib/chart-theme';
import { doughnutLabelsPlugin } from '../lib/doughnut-labels';
import { dataLabelsPlugin } from '../lib/data-labels';
import { useTheme } from '../hooks/useTheme';
import { useFilters } from '../hooks/useFilters';
import { Card } from './primitives';
import { EmptyState, PartialNotice, UnavailableNotice } from './states';

setupCharts();

/**
 * Presentación de un `ChartPayload`.
 *
 * El frontend NO calcula nada: recibe etiquetas, series y estado ya resueltos por
 * el motor de indicadores. Un punto con `value: null` se dibuja como hueco, jamás
 * como cero, y el estado global del gráfico se anuncia por encima del lienzo.
 */
export interface ChartCardProps {
  payload: ChartPayload;
  small?: boolean;
  help?: string;
}

function buildData(
  payload: ChartPayload,
  theme: ChartTheme,
): ChartData<ChartType, Array<number | null>, string> {
  const isDoughnut = payload.type === 'doughnut';
  const isLine = payload.type === 'line';

  if (isDoughnut) {
    const first = payload.series[0];
    return {
      labels: payload.labels,
      datasets: [
        {
          label: first?.label ?? payload.title,
          data: (first?.points ?? []).map((point) => point.value),
          backgroundColor: payload.labels.map(
            (_, index) => theme.series[index % theme.series.length] ?? theme.series[0] ?? '#1F6FB2',
          ),
          borderColor: 'transparent',
          borderWidth: 0,
        },
      ],
    };
  }

  return {
    labels: payload.labels,
    datasets: payload.series.map((series, index) => {
      const color = theme.series[index % theme.series.length] ?? '#1F6FB2';
      return {
        label: series.label,
        data: series.points.map((point) => point.value),
        backgroundColor: isLine ? withAlpha(color, 0.14) : color,
        borderColor: color,
        borderWidth: isLine ? 2 : 0,
        borderRadius: isLine ? 0 : 4,
        fill: isLine && payload.series.length === 1,
        tension: 0.28,
        pointRadius: 3,
        pointHoverRadius: 5,
        // Un hueco no se une con una línea recta: eso inventaría una tendencia.
        spanGaps: false,
        maxBarThickness: 42,
      };
    }),
  };
}

export function ChartCard({ payload, small = false, help }: ChartCardProps): JSX.Element {
  const { theme: themeName } = useTheme();
  const { filters, setFilter, options } = useFilters();
  const [chartTheme, setChartTheme] = useState<ChartTheme>(() => readChartTheme());

  useEffect(() => {
    // Los tokens ya están aplicados en :root cuando cambia `data-theme`.
    setChartTheme(readChartTheme());
  }, [themeName]);

  const chartType: ChartType = payload.type === 'horizontalBar' ? 'bar' : payload.type;
  const data = useMemo(() => buildData(payload, chartTheme), [payload, chartTheme]);
  /**
   * El complemento de etiquetas se aplica SOLO a los anillos.
   *
   * Se pasa por instancia y no se registra globalmente: registrado a lo ancho de
   * Chart.js se ejecutaría también en barras y líneas, donde `getDatasetMeta(0).data`
   * son barras y el dibujo no tendría sentido.
   */
  const plugins = useMemo(
    () =>
      payload.type === 'doughnut'
        ? [doughnutLabelsPlugin(chartTheme)]
        : [dataLabelsPlugin(chartTheme, inferChartFormat(payload))],
    [payload, chartTheme],
  );

  /**
   * Clic sobre una porción o barra para FILTRAR por esa categoría.
   *
   * ── Cuándo se activa y por qué solo entonces ───────────────────────────────
   * Solo en los gráficos cuya clave termina en `ByCompany` o `ByArea`, porque son
   * los únicos cuyas etiquetas corresponden a un filtro existente. Hacer clicable
   * un gráfico por mes o por tramo etario prometería una acción que no existe, y un
   * elemento que parece pulsable y no hace nada es peor que uno que no lo parece.
   *
   * La etiqueta se traduce a identificador con el catálogo de filtros. Si el nombre
   * no aparece —un área desactivada, por ejemplo— no se hace nada: aplicar un filtro
   * con un identificador inventado devolvería un conjunto vacío y parecería que no
   * hay datos.
   */
  const dimensionFiltrable = useMemo((): 'companyId' | 'areaId' | null => {
    if (/ByCompany$/i.test(payload.key)) return 'companyId';
    if (/ByArea$/i.test(payload.key)) return 'areaId';
    return null;
  }, [payload.key]);

  const alSeleccionar = useMemo(() => {
    if (dimensionFiltrable === null) return undefined;

    return (etiqueta: string): void => {
      const catalogo =
        dimensionFiltrable === 'companyId' ? options?.companies : options?.areasByCompany;

      const opcion = catalogo?.find((o) => o.label === etiqueta);
      if (!opcion) return;

      // Volver a pulsar la misma categoría LIMPIA el filtro. Sin esto habría que ir
      // a los desplegables de arriba para deshacer un clic dado en el gráfico.
      const actual = filters[dimensionFiltrable];
      setFilter(dimensionFiltrable, actual === opcion.id ? undefined : opcion.id);
    };
  }, [dimensionFiltrable, options, filters, setFilter]);

  const chartOptions = useMemo(() => {
    const base = buildChartOptions(payload, chartTheme);

    if (!alSeleccionar) return base as ChartOptions<ChartType>;

    return {
      ...base,
      // El puntero cambia a mano sobre los elementos pulsables: sin esa señal nadie
      // descubre que el gráfico responde al clic.
      onHover: (evento, elementos) => {
        const lienzo = evento.native?.target as HTMLElement | undefined;
        if (lienzo) lienzo.style.cursor = elementos.length > 0 ? 'pointer' : 'default';
      },
      onClick: (_evento, elementos, grafico) => {
        const primero = elementos[0];
        if (!primero) return;
        const etiqueta = grafico.data.labels?.[primero.index];
        if (typeof etiqueta === 'string') alSeleccionar(etiqueta);
      },
    } as ChartOptions<ChartType>;
  }, [payload, chartTheme, alSeleccionar]);

  /**
   * Firma de los datos: cambia solo cuando cambian las etiquetas o los valores.
   *
   * NO se usa el objeto completo ni un contador: un refresco que devuelve lo mismo
   * no debe remontar el gráfico, o parpadearía cada vez que la caché se revalida.
   */
  const firmaDeDatos = useMemo(
    () =>
      [
        payload.key,
        payload.labels.join('|'),
        payload.series.map((s) => s.points.map((p) => p.value ?? 'n').join(',')).join(';'),
      ].join('::'),
    [payload],
  );

  const hasPoints = payload.series.some((series) =>
    series.points.some((point) => point.value !== null),
  );

  return (
    <Card title={payload.title} help={help}>
      {payload.status === 'NOT_AVAILABLE' ? (
        <UnavailableNotice note={payload.note} />
      ) : (
        <>
          {payload.status === 'PARTIAL' && <PartialNotice note={payload.note} />}
          {payload.status === 'NO_RECORDS' || !hasPoints ? (
            <EmptyState
              title="Sin registros"
              message={
                payload.note ??
                'No hubo registros en el período seleccionado, por lo que no hay serie que graficar.'
              }
            />
          ) : (
            <div
              className={
                `chartbox${small ? ' chartbox--sm' : ''}` +
                // Mismo umbral que decide la posición de la leyenda en
                // `chart-theme`: si se separan, la leyenda baja y el contenedor no
                // crece, y el anillo queda aplastado.
                (payload.type === 'doughnut' && payload.labels.length > 8
                  ? ' chartbox--legend-below'
                  : '')
              }
              /**
               * Las barras HORIZONTALES crecen con la cantidad de categorías.
               *
               * «Dotación por área» tiene 31 áreas: en la altura fija de 268 px cada
               * barra quedaría de ocho píxeles, con las etiquetas encimadas. Se
               * reserva un alto por barra y se deja un mínimo para los casos con
               * pocas.
               *
               * Solo aplica a las horizontales: en las verticales las categorías
               * crecen a lo ANCHO, y estirar el alto solo dejaría espacio vacío.
               */
              style={
                payload.type === 'horizontalBar' && payload.labels.length > 6
                  ? { height: `${Math.max(268, payload.labels.length * 22 + 70)}px` }
                  : undefined
              }
            >
              <ReactChart
                /**
                 * La firma de los DATOS forma parte de la clave de React.
                 *
                 * ── El defecto que corrige ─────────────────────────────────────
                 * Al aplicar un filtro, los KPI y la tabla se actualizaban y los
                 * gráficos NO: con la empresa filtrada en PCH, el indicador decía 45
                 * y el anillo seguía mostrando 200 y las 31 áreas del grupo. Es la
                 * peor forma del error, porque una cifra correcta al lado de un
                 * gráfico obsoleto hace confiar en el gráfico.
                 *
                 * `react-chartjs-2` conserva la instancia de Chart.js entre
                 * renderizados y la actualiza en el sitio; con el complemento propio
                 * y las opciones reconstruidas esa actualización no se reflejaba.
                 * Incluir la firma en la clave fuerza a montar el gráfico de nuevo
                 * cuando los datos cambian de verdad.
                 *
                 * El coste es la animación de transición entre dos conjuntos de
                 * datos. Es un intercambio claro: un gráfico que se anima hacia una
                 * cifra equivocada no vale más que uno que aparece con la correcta.
                 */
                key={firmaDeDatos}
                type={chartType}
                data={data}
                options={chartOptions}
                plugins={plugins as never}
              />
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/** Rejilla de gráficos del dashboard: dos por fila, apiladas bajo 1100 px. */
export function ChartGrid({ charts, small }: { charts: ChartPayload[]; small?: boolean }): JSX.Element {
  return (
    <div className="row row--2e">
      {charts.map((chart) => (
        <ChartCard key={chart.key} payload={chart} small={small} />
      ))}
    </div>
  );
}
